import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/how-ann-works.css";
import useReveal from "./useReveal";

export default function HowAnnWorks() {
  const ref = useReveal();
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const md = root.querySelector("#miniDial");
    if (!md) return undefined;

    if (!("IntersectionObserver" in window)) {
      md.classList.add("built");
      return undefined;
    }

    const ob = new IntersectionObserver(
      (entries, o) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            md.classList.add("built");
            o.unobserve(md);
          }
        });
      },
      { threshold: 0.5 }
    );
    ob.observe(md);

    return () => ob.disconnect();
  }, []);

  const setRefs = (node) => {
    rootRef.current = node;
    ref.current = node;
  };

  return (
    <div className="page ann-subpage" ref={setRefs}>
      {/* HERO */}
      <section className="sub-hero" id="top">
        <div className="frame">
          <Link to="/" className="back">← Ann · Hiring intelligence</Link>
          <span>How it works</span>
        </div>
        <span className="eye">How Ann works</span>
        <h1>Six moves. <em>One</em> intelligence<span className="period" /></h1>
        <p className="lede-xl">
          Ann runs the whole search — from reading your role to recommending a hire — as <b>one connected intelligence</b>, not six tools stitched together. Here's each move, and how the candidate your funnel never showed you finally surfaces.
        </p>
        <div className="ctas">
          <a href="#acts" className="btn btn-primary btn-lg">Walk through it</a>
          <Link to="/why-ann" className="btn btn-secondary btn-lg">Why it's different</Link>
        </div>
      </section>

      {/* THE SIX ACTS */}
      <section className="acts" id="acts">
        <div className="sub-head">
          <span className="eye">The six acts</span>
          <h2 className="sub-h2">What Ann actually <em>does</em>.</h2>
          <p className="lede">For every role, every time — whether you're filling two hundred seats or headhunting one.</p>
        </div>

        {/* I · Listen */}
        <div className="act-block">
          <div className="copy">
            <div className="n"><span className="ix">I</span> Listen</div>
            <h3>Reads the <em>company</em> and the role.</h3>
            <p>Before a single profile, Ann learns how you build, who you've hired well, and what the role really involves. <b>A "Senior CS Manager" becomes "leads 40 reps through a Q4 surge"</b> — the work, not the title. Everything downstream is measured against this.</p>
          </div>
          <div className="mv">
            <div className="mvt">CompanyDNA + RoleDNA</div>
            <div className="chips">
              <span className="chip cy">Ships weekly</span>
              <span className="chip cy">Operators &gt; strategists</span>
              <span className="chip">Leads 40 reps</span>
              <span className="chip">Owns Q4 surge</span>
              <span className="chip">De-escalation</span>
            </div>
          </div>
        </div>

        {/* II · Search */}
        <div className="act-block">
          <div className="copy">
            <div className="n"><span className="ix">II</span> Search</div>
            <h3>Finds them with <em>two engines</em>.</h3>
            <p><b>QuickSearch</b> sweeps the open market at volume. <b>TalentGPS</b> headhunts the people who aren't actively looking and don't surface to keywords. Together they cover the whole market — including the roughly six in ten candidates a standard search never sees.</p>
          </div>
          <div className="mv">
            <div className="mvt">Two engines · one role</div>
            <div className="twoeng">
              <div className="eng"><div className="k">QuickSearch</div><div className="v">1,840</div><div className="d">open-market, at volume</div></div>
              <div className="eng"><div className="k">TalentGPS</div><div className="v">71</div><div className="d">headhunted, passive</div></div>
            </div>
          </div>
        </div>

        {/* III · Read */}
        <div className="act-block">
          <div className="copy">
            <div className="n"><span className="ix">III</span> Read</div>
            <h3>Reads every résumé like a <em>human</em>.</h3>
            <p>Not keyword-matching — reading for the work. Ann sees what a careful recruiter would: the cross-trained, the under-titled, the plainly-described. <b>Same care on the first résumé and the ten-thousandth.</b> It never skims, and it never tires.</p>
          </div>
          <div className="mv">
            <div className="mvt">What Ann reads past the words</div>
            <div className="rd-line2">"Handled escalations on the support floor."</div>
            <div className="rd-note2"><span className="g" /><span className="t">De-escalation lead across 40 reps — not just "handled".</span></div>
          </div>
        </div>

        {/* IV · Reach out */}
        <div className="act-block">
          <div className="copy">
            <div className="n"><span className="ix">IV</span> Reach out</div>
            <h3>Reaches out in <em>your</em> voice.</h3>
            <p>Ann drafts the outreach in your team's tone. You approve before anything sends. <b>The candidate hears from a voice that sounds like the person who'll meet them</b> — because it is. No spray-and-pray, no message that reads like a robot wrote it.</p>
          </div>
          <div className="mv">
            <div className="mvt">Draft · awaiting your approval</div>
            <div className="draft">
              <div className="meta">To Ravi · Tue 9:14a</div>
              "Hi Ravi — your work owning the West-region pipeline at your last role lines up closely with a search we're running. Worth a 20-minute call this week?"
            </div>
          </div>
        </div>

        {/* V · Interview */}
        <div className="act-block">
          <div className="copy">
            <div className="n"><span className="ix">V</span> Interview</div>
            <h3>Interviews with <em>structure</em>.</h3>
            <p>Structured, recorded, and scored against the same rubric for every candidate. <b>Round-three Ravi is comparable to round-three Sneha six weeks later</b> — because they answered the same questions, judged the same way. Interviews stop being theatre and start being data.</p>
          </div>
          <div className="mv">
            <div className="mvt">Same rubric · every candidate</div>
            <div className="rub"><span className="lbl">System ownership</span><span className="bar"><i style={{ width: "90%" }} /></span><span className="sc">A−</span></div>
            <div className="rub"><span className="lbl">Communication</span><span className="bar"><i style={{ width: "95%" }} /></span><span className="sc">A</span></div>
            <div className="rub"><span className="lbl">Stakeholder mgmt</span><span className="bar"><i style={{ width: "62%", background: "oklch(0.70 0.13 65)" }} /></span><span className="sc" style={{ color: "oklch(0.55 0.13 65)" }}>B−</span></div>
          </div>
        </div>

        {/* VI · Recommend */}
        <div className="act-block">
          <div className="copy">
            <div className="n"><span className="ix">VI</span> Recommend</div>
            <h3>Recommends, with <em>the case</em>.</h3>
            <p>A confidence report: the verdict, the evidence behind it, the gaps that don't fit. <b>The decision stays yours</b> — Ann never makes the call. It just makes sure you never make it blind, and that you can defend it the day someone asks how.</p>
          </div>
          <div className="mv" id="recMv">
            <div className="mvt">Hiring confidence report</div>
            <div className="minidial" id="miniDial">
              <div className="ring"><span className="num">88</span></div>
              <div className="vr"><b>Recommend hire.</b> Strong on the must-haves; one gap to probe in round 2.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CONNECTIVE TISSUE */}
      <section className="sub-section dark tissue">
        <div className="cols-split">
          <div>
            <span className="eye" style={{ display: "block", marginBottom: 24 }}>The part that matters</span>
            <p className="statement">The <em>connection</em> between the moves is the product.</p>
          </div>
          <div className="prose">
            <p className="big" style={{ color: "oklch(0.88 0.010 260)" }}>Anyone can sell you a sourcing tool, a screening tool, and an interview tool. Stitched together, they leak signal at every seam.</p>
            <p style={{ color: "oklch(0.82 0.010 260)" }}>What Ann learns in <b style={{ color: "var(--c-paper)" }}>Listen</b> shapes <b style={{ color: "var(--c-paper)" }}>Search</b>; what surfaces in <b style={{ color: "var(--c-paper)" }}>Read</b> frames <b style={{ color: "var(--c-paper)" }}>Interview</b>; what the interview proves becomes the <b style={{ color: "var(--c-paper)" }}>recommendation</b>. Nothing dropped, nothing repeated.</p>
            <p style={{ color: "oklch(0.82 0.010 260)" }}><em style={{ color: "var(--c-cyan)" }}>That's why it has to be one intelligence.</em> The judgement compounds. Six tools can't do that, no matter how well they integrate.</p>
          </div>
        </div>
      </section>

      {/* WHERE TO START / MODES */}
      <section className="sub-section bone">
        <div className="sub-head">
          <span className="eye">Where to start</span>
          <h2 className="sub-h2">Take the whole pipeline. Or just the part you <em>need</em>.</h2>
          <p className="lede">You don't have to adopt all six at once. Start where the bottleneck is.</p>
        </div>
        <div className="modes-grid">
          <div className="mode">
            <div className="lbl">Just sourcing</div>
            <h3>Sourcing.</h3>
            <p>Ann fills the top of your funnel with the people you're missing. You keep your own evaluation and interviews.</p>
            <div className="steps"><div className="step on">Find</div><div className="step off">Eval</div><div className="step off">Inter</div><div className="step off">Dec</div></div>
          </div>
          <div className="mode featured">
            <div className="lbl">All of it · recommended</div>
            <h3>The <em>whole thing</em>.</h3>
            <p>Hand Ann the role; get back a decision-ready shortlist with the case for each name. The connective tissue at full strength.</p>
            <div className="steps"><div className="step on">Find</div><div className="step on">Eval</div><div className="step on">Inter</div><div className="step on">Dec</div></div>
          </div>
          <div className="mode">
            <div className="lbl">Just interviews</div>
            <h3>Interviews.</h3>
            <p>Bring your own pipeline; Ann runs the rounds comparable and produces the confidence report.</p>
            <div className="steps"><div className="step off">Find</div><div className="step off">Eval</div><div className="step on">Inter</div><div className="step on">Dec</div></div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">See the six moves on a real role</span>
        <h2>Watch Ann run <em>your</em> search.</h2>
        <p className="sub">Fifteen minutes, on a live role off your desk. From the first read to the confidence report.</p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/why-ann" className="btn btn-secondary btn-lg">Why Ann is different</Link>
        </div>
      </section>
    </div>
  );
}
